import "./slideshow.css";

import React from "react";

import { Chevron } from "./Icons";

const prefersReducedMotion = () => window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

//Photo slideshow with arrows, swiping and arrow keys. Used for the page banners ("hero"), the Home room tabs ("panel")
//and the style cards on the room pages ("card"). With onOpen, clicking a photo opens it in the full-screen viewer
const Slideshow = ({ photos, label, variant = "panel", autoPlay = 0, sizes = "100vw", onOpen = null, showDots = false }) => {
  const [current, setCurrent] = React.useState(0);
  const [loadUpTo, setLoadUpTo] = React.useState(1);
  const [isPaused, setIsPaused] = React.useState(false);
  const touchStart = React.useRef(null);
  const count = photos.length;

  //Photos load as the visitor reaches them (plus the next one, so it's ready). Going back from the first photo loads the rest
  React.useEffect(() => {
    setLoadUpTo((n) => Math.max(n, current + 1, current === count - 1 ? count : 0));
  }, [current, count]);

  const go = React.useCallback((step) => setCurrent((c) => (c + step + count) % count), [count]);

  //Moves on by itself (banner only), pausing while the mouse / keyboard focus is on it or the tab is hidden
  React.useEffect(() => {
    if (!autoPlay || isPaused || count < 2 || prefersReducedMotion()) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      if (!document.hidden) {
        go(1);
      }
    }, autoPlay);
    return () => window.clearInterval(timer);
  }, [autoPlay, isPaused, count, go]);

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) {
      return;
    }
    const dx = e.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(dx) > 50) {
      go(dx < 0 ? 1 : -1);
    }
  };

  return (
    <section
      className={`slideshow is-${variant}`}
      aria-roledescription={"carousel"}
      aria-label={label}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") {
          go(1);
        }
        else if (e.key === "ArrowLeft") {
          go(-1);
        }
      }}
    >
      <ul
        className={"slideshow-track"}
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {photos.map((photo, i) => {
          const img = i <= loadUpTo ? (
            <img
              src={photo.md}
              srcSet={photo.srcSet}
              sizes={sizes}
              alt={photo.alt}
              width={photo.width}
              height={photo.height}
            />
          ) : null;
          return (
            <li
              key={`${photo.id}-${i}`}
              className={"slideshow-slide"}
              aria-roledescription={"slide"}
              aria-label={`${i + 1} of ${count}`}
              aria-hidden={i !== current}
            >
              {onOpen ? (
                <button
                  type={"button"}
                  className={"slideshow-open"}
                  aria-label={`View ${photo.alt} full size`}
                  tabIndex={i === current ? 0 : -1}
                  onClick={() => onOpen(i)}
                >
                  {img}
                </button>
              ) : img}
            </li>
          );
        })}
      </ul>

      {count > 1 ? (
        <React.Fragment>
          <button
            type={"button"}
            className={"slideshow-arrow is-prev"}
            aria-label={"Previous photo"}
            onClick={() => go(-1)}
          >
            <Chevron />
          </button>
          <button
            type={"button"}
            className={"slideshow-arrow is-next"}
            aria-label={"Next photo"}
            onClick={() => go(1)}
          >
            <Chevron isNext />
          </button>
        </React.Fragment>
      ) : null}

      {showDots && count > 1 ? (
        <div className={"slideshow-dots"}>
          {photos.map((photo, i) => (
            <button
              key={`${photo.id}-${i}`}
              type={"button"}
              className={`slideshow-dot ${i === current ? "is-current" : ""}`}
              aria-label={`Show photo ${i + 1}`}
              aria-current={i === current ? "true" : undefined}
              onClick={() => setCurrent(i)}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default Slideshow;
